import type { Ad } from "@/schemas";
import type { AxiosResponse } from "axios";

/** Time (ms) window to not display an ad if it's about to expire. */
const AD_EXPIRY_THRESHOLD = 5 * 60 * 1000;

/**
 * Extra px Virtuoso keeps mounted around the viewport on ad-bearing feeds,
 * so ads (and their impressions) preload before they scroll into view.
 */
const AD_PRELOAD_VIEWPORT_PX = { top: 600, bottom: 1800 };

const GAM_REQUEST_LOOKAHEAD_ROOT_MARGIN = "0px 0px 50% 0px";

/** Ads shown before any content on logged-out feeds. */
const LOGGED_OUT_LEAD_AD_COUNT = 3;

/** Whether a feed item is an ad placeholder spliced in by formatResultWithAds. */
const isAdPlaceholderId = (id: string): boolean => id.endsWith("-ad");

/**
 * Stable slot keys for feeds that render ads where the backend placed
 * placeholders. Placeholder ids regenerate on every refetch, so keys that
 * disappear release their ad back to the pool.
 */
const getPlaceholderAdSlotKeys = (
  statuses: readonly { id: string }[],
): string[] =>
  statuses.filter((status) => isAdPlaceholderId(status.id)).map((s) => s.id);

/** Slot key for the Nth ad shown before any content on a logged-out feed. */
const leadAdSlotKey = (index: number): string => `lead-${index}`;

/** Slot key for the ad shown right after a content item on a logged-out feed. */
const afterStatusAdSlotKey = (statusId: string): string => `after-${statusId}`;

/**
 * Stable slot keys for the logged-out strategy: a fixed lead block, then one
 * slot after every content item.
 */
const getLoggedOutAdSlotKeys = (
  statuses: readonly { id: string }[],
): string[] => {
  const keys: string[] = [];
  for (let i = 0; i < LOGGED_OUT_LEAD_AD_COUNT; i++)
    keys.push(leadAdSlotKey(i));
  statuses.forEach((status) => {
    if (!isAdPlaceholderId(status.id))
      keys.push(afterStatusAdSlotKey(status.id));
  });
  return keys;
};

/** Whether the ad is expired or about to expire. */
const isExpired = (
  metrics: NonNullable<Ad["ad"]>["metrics"],
  threshold = AD_EXPIRY_THRESHOLD,
): boolean => {
  if (metrics.expires_at) {
    const now = new Date();
    return now.getTime() > new Date(metrics.expires_at).getTime() - threshold;
  } else {
    return false;
  }
};

/** Modify the impression URL and attach a source identifier (ie 'home', 'profile', etc). */
const addSource = (impressionUrl: string, source: string): string => {
  const url = new URL(impressionUrl);
  url.searchParams.append("source", source);
  return url.toString();
};

/** Get ad indexes from X-Truth-Ad-Indexes header. */
const adIndexesFromHeader = (response: AxiosResponse): number[] => {
  const header = response.headers["x-truth-ad-indexes"] as string | undefined;
  const strIndexes = header?.split(",") ?? [];
  return strIndexes.map((strIndex) => Number(strIndex.trim()));
};

const formatResultWithAds = <T extends { id: string }>(
  result: T[],
  response: AxiosResponse,
) => {
  const isAdsSupported = result.length >= 4;
  if (!isAdsSupported) {
    return result;
  }

  const adIndexes = adIndexesFromHeader(response);

  // Inject ad indexes into the array of Statuses.
  return result.reduce((acc, status, index) => {
    const time = new Date().getTime();

    if (adIndexes.includes(index)) {
      acc.push({ id: `${time}-${index}-ad` } as T);
    }

    acc.push(status);
    return acc;
  }, [] as T[]);
};

export {
  AD_PRELOAD_VIEWPORT_PX,
  GAM_REQUEST_LOOKAHEAD_ROOT_MARGIN,
  LOGGED_OUT_LEAD_AD_COUNT,
  isAdPlaceholderId,
  leadAdSlotKey,
  afterStatusAdSlotKey,
  getPlaceholderAdSlotKeys,
  getLoggedOutAdSlotKeys,
  isExpired,
  addSource,
  adIndexesFromHeader,
  formatResultWithAds,
};
